import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, MagnifyingGlass, User, Phone, MapPin, WifiHigh, CalendarBlank, Clock, Check, Warning, CaretRight, Headset, Wrench, Path, ShieldCheck, CalendarCheck, Robot, ArrowsClockwise } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CallCenterWifiReport from '@/pages/CallCenterWifiReport';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const ISSUES = ['No Internet', 'Red Light No Internet', 'Slow Speed', 'Router Problem', 'Fiber Cut', 'New Installation', 'Billing Question', 'ONU Issue', 'Other'];
const PRIORITIES = ['Normal', 'Urgent', 'Require'];
const MANUAL_PRIORITIES = ['Urgent', 'Require']; // Only these allow manual schedule
const TEAMS = [
  { value: 'IMFT', label: 'Service & Installation Team', icon: '🔧' },
  { value: 'BMFT', label: 'Backbone Fiber Team', icon: '🔗' },
  { value: 'NOC', label: 'Network NOC Team', icon: '🛡' },
];
const STATUSES = ['New', 'Scheduled', 'Pending', 'Assigned', 'In Progress', 'Resolved', 'Closed', 'Cancelled'];
const STATUS_CLR = { New: 'bg-[#E3F2FD] text-[#0D47A1]', Scheduled: 'bg-[#E0F2F1] text-[#00695C]', Pending: 'bg-[#FFF8E1] text-[#F57F17]', Assigned: 'bg-[#EDE7F6] text-[#4527A0]', 'In Progress': 'bg-[#E8EAF6] text-[#283593]', Resolved: 'bg-[#E8F5E9] text-[#1B5E20]', Closed: 'bg-[#ECEFF1] text-[#37474F]', Cancelled: 'bg-[#FFEBEE] text-[#B71C1C]' };
const TICKET_STATUS_DISPLAY_ALIASES = { Completed: 'Resolved' };
const displayTicketStatus = (status) => TICKET_STATUS_DISPLAY_ALIASES[status] || status;
const PRI_CLR = { Critical: 'text-[#B71C1C]', Urgent: 'text-[#B71C1C]', High: 'text-[#E65100]', Require: 'text-[#E65100]', Medium: 'text-[#0D47A1]', Normal: 'text-[#0D47A1]', Low: 'text-[var(--md-on-surface-variant)]' };
const ISSUE_TEAM_MAP = { 'No Internet': 'IMFT', 'Red Light No Internet': 'IMFT', 'Slow Speed': 'IMFT', 'Router Problem': 'IMFT', 'ONU Issue': 'IMFT', 'Fiber Cut': 'BMFT', 'New Installation': 'IMFT', 'Billing Question': '', 'Other': '' };

function PppoeCallHistoryPanel() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_day: {}, by_user: {} });
  const todayMyanmar = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Yangon', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const [filter, setFilter] = useState({ pppoe_id: '', date: todayMyanmar, date_from: '', date_to: '' });
  const [loading, setLoading] = useState(false);
  const load = async (f = filter) => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: '2000' });
      Object.entries(f).forEach(([k, v]) => { if (v) q.set(k, v); });
      const tok = localStorage.getItem('session_token');
      const res = await fetch(`${API}/operator/pppoe-call-history?${q.toString()}`, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
      if (!res.ok) throw new Error('history request failed');
      const data = await res.json();
      setRows(data.rows || []); setSummary(data);
    } catch (e) { console.error(e); toast.error('PPPoE call history could not be loaded'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const set = (key, value) => setFilter(prev => ({ ...prev, [key]: value }));
  return <div className="space-y-4">
    <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div><h2 className="text-base font-bold">PPPoE Call History</h2><p className="text-xs text-[var(--md-on-surface-variant)] mt-1">PPPoE ID တစ်ခုချင်းစီအလိုက် ဖုန်းဆက်ထားသော Service history ကို နေ့ရက်လိုက်ကြည့်နိုင်ပါသည်။</p></div>
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-[11px]">PPPoE ID<input value={filter.pppoe_id} onChange={e => set('pppoe_id', e.target.value)} placeholder="PPPoE ID" className="md3-input h-8 text-xs block w-52" /></label>
          <label className="text-[11px]">နေ့ရက်<input type="date" value={filter.date} onChange={e => set('date', e.target.value)} className="md3-input h-8 text-xs block" /></label>
          <button onClick={() => load()} className="md3-filled-btn text-xs h-8">Search</button>
          <button onClick={() => { const f = { pppoe_id: '', date: '', date_from: '', date_to: '' }; setFilter(f); load(f); }} className="md3-outlined-btn text-xs h-8">Clear</button>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3"><div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-[var(--md-on-surface-variant)]">Total Calls</p><p className="text-xl font-bold text-[#0D47A1]">{summary.total || 0}</p></div><div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-[var(--md-on-surface-variant)]">PPPoE Users</p><p className="text-xl font-bold text-[#00695C]">{Object.keys(summary.by_user || {}).length}</p></div><div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-[var(--md-on-surface-variant)]">Days Covered</p><p className="text-xl font-bold text-[#6A1B9A]">{Object.keys(summary.by_day || {}).length}</p></div><div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-[var(--md-on-surface-variant)]">Status</p><p className="text-sm font-bold text-[#006D3B] mt-1">{loading ? 'Loading...' : 'Ready'}</p></div></div>
    <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] overflow-hidden shadow-sm"><div className="px-4 py-3 bg-[var(--md-surface-1)] border-b"><h3 className="text-sm font-semibold">နေ့ရက်လိုက် Phone / Service History</h3></div><div className="overflow-x-auto"><table className="w-full"><thead><tr className="bg-[var(--md-surface-1)]"><th className="text-left text-[11px] px-4 py-3">Date / Time</th><th className="text-left text-[11px] px-4 py-3">PPPoE ID</th><th className="text-left text-[11px] px-4 py-3">Customer</th><th className="text-left text-[11px] px-4 py-3">Service / Issue</th><th className="text-left text-[11px] px-4 py-3">Call Result</th><th className="text-left text-[11px] px-4 py-3">Operator</th><th className="text-left text-[11px] px-4 py-3">Note</th></tr></thead><tbody>{loading ? <tr><td colSpan={7} className="text-center py-10 text-sm">Loading history...</td></tr> : rows.length === 0 ? <tr><td colSpan={7} className="text-center py-10 text-sm text-[var(--md-on-surface-variant)]">ဖုန်းခေါ်ဆိုမှု history မတွေ့သေးပါ</td></tr> : rows.map((r, i) => <tr key={r.note_id || `${r.ticket_id}-${i}`} className="border-t hover:bg-[var(--md-surface-1)]"><td className="px-4 py-3 text-xs whitespace-nowrap">{r.called_at ? new Date(r.called_at).toLocaleString() : r.day}</td><td className="px-4 py-3"><span className="font-mono text-[11px] font-bold text-[#1565C0]">{r.pppoe_id || '-'}</span></td><td className="px-4 py-3 text-xs">{r.customer_name || '-'}<br/><span className="text-[10px] text-[var(--md-on-surface-variant)]">{r.customer_phone || ''}</span></td><td className="px-4 py-3 text-xs">{r.issue_type || '-'}<br/><span className="text-[10px]">Ticket: {r.ticket_id}</span></td><td className="px-4 py-3"><span className="md3-chip text-[10px] bg-[#E8F5E9] text-[#1B5E20]">{r.call_status || 'Connected'}</span></td><td className="px-4 py-3 text-xs">{r.operator_name || r.operator_id || '-'}</td><td className="px-4 py-3 text-xs max-w-xs">{r.summary && <strong className="block">{r.summary}</strong>}{r.note || '-'}</td></tr>)}</tbody></table></div></div>
  </div>;
}

export default function OperatorModule() {
  const { user } = useAuth();
  const [tab, setTab] = useState('tickets');
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('all');
  const [statFilter, setStatFilter] = useState(null);
  const [infraDown, setInfraDown] = useState({ active_tasks: [], ongoing_issues: [] });
  const [searchQ, setSearchQ] = useState('');
  // Create ticket state
  const [custSearch, setCustSearch] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [searching, setSearching] = useState(false);
  const [availability, setAvailability] = useState(null);
  const searchTimer = useRef(null);
  const [form, setForm] = useState({ issue_type: '', priority: 'Normal', description: '', assign_team: '', service_date: '', service_time: '' });
  // Transfer modal state
  const [transferTicket, setTransferTicket] = useState(null);
  const [transferForm, setTransferForm] = useState({ route_to: 'cs', noc_note: '' });
  const [transferring, setTransferring] = useState(false);
  // Append-only operator call-note modal
  const [callNoteTicket, setCallNoteTicket] = useState(null);
  const [callNotes, setCallNotes] = useState([]);
  const [callNoteForm, setCallNoteForm] = useState({ summary: '', note: '', call_status: 'Connected', next_follow_up_at: '' });
  const [callNoteSaving, setCallNoteSaving] = useState(false);
  const [deptFilter, setDeptFilter] = useState(null);
  // Auto-schedule state
  const [scheduleTab, setScheduleTab] = useState('calendar');
  const [teamSchedule, setTeamSchedule] = useState([]);
  const [scheduleViewDate, setScheduleViewDate] = useState(new Date().toISOString().slice(0,10));
  const [autoScheduleRunning, setAutoScheduleRunning] = useState(false);
  const [autoScheduleResult, setAutoScheduleResult] = useState(null);
  // Schedule datetime picker state
  const [scheduleTicketId, setScheduleTicketId] = useState(null);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleDateFilter, setScheduleDateFilter] = useState('');
  const [sortBySchedule, setSortBySchedule] = useState(false);
  const [externalHistory, setExternalHistory] = useState([]);
  const [boxDownReview, setBoxDownReview] = useState([]);
  const [externalLoading, setExternalLoading] = useState(false);
  const calculatedStatusCounts = STATUSES.reduce((counts, status) => ({ ...counts, [status]: 0 }), {});
  tickets.forEach(ticket => {
    const status = displayTicketStatus(ticket.status);
    if (Object.prototype.hasOwnProperty.call(calculatedStatusCounts, status)) calculatedStatusCounts[status] += 1;
  });
  const statusCounts = stats.status_counts || calculatedStatusCounts;

  const load = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [tRes, sRes, idRes] = await Promise.all([
        fetch(`${API}/service-tickets`, { headers }),
        fetch(`${API}/service-tickets/stats/overview`, { headers }),
        fetch(`${API}/bmft/infra-down-active`, { headers })
      ]);
      if (tRes.ok) { const _d = await tRes.json(); setTickets(Array.isArray(_d) ? _d : []); };
      if (sRes.ok) setStats(await sRes.json());
      if (idRes.ok) setInfraDown(await idRes.json());
    } catch (e) { console.error(e); }
  };
  useEffect(() => {
    const requestedStatus = new URLSearchParams(window.location.search).get('status');
    if (STATUSES.includes(requestedStatus)) { setFilter(requestedStatus); setTab('tickets'); }
    load();
    loadTeamSchedule();
  }, []);

  const loadTeamSchedule = async (date) => {
    const d = date || new Date().toISOString().slice(0,10);
    try {
      const res = await fetch(`${API}/service-tickets/team-schedule?date=${d}&days=7`);
      if (res.ok) setTeamSchedule((await res.json()).teams || []);
    } catch (e) { console.error(e); }
  };

  const runAutoSchedule = async (dryRun = false) => {
    setAutoScheduleRunning(true);
    setAutoScheduleResult(null);
    try {
      const res = await fetch(`${API}/service-tickets/auto-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_date: new Date().toISOString().slice(0,10), dry_run: dryRun })
      });
      const data = await res.json();
      setAutoScheduleResult(data);
      if (!dryRun) { toast.success(data.message || 'Auto-schedule complete'); load(); loadTeamSchedule(); }
      else { toast.info(`Preview: ${data.message}`); }
    } catch { toast.error('Auto-schedule failed'); }
    finally { setAutoScheduleRunning(false); }
  };
  const getAuthHeaders = () => {
    const tok = localStorage.getItem('session_token');
    return tok ? { 'Authorization': `Bearer ${tok}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };
  const loadExternalHistory = async () => {
    setExternalLoading(true);
    try {
      const headers = getAuthHeaders();
      const [hRes, bRes] = await Promise.all([
        fetch(`${API}/operator/external-history?limit=500`, { headers }),
        fetch(`${API}/operator/box-down-review?limit=500`, { headers })
      ]);
      if (hRes.ok) setExternalHistory(await hRes.json());
      if (bRes.ok) setBoxDownReview(await bRes.json());
    } catch (e) { console.error(e); toast.error('Imported Operator history could not be loaded'); }
    finally { setExternalLoading(false); }
  };
  useEffect(() => { if (tab === 'external-history' || tab === 'box-down-review') loadExternalHistory(); }, [tab]);
  const openCallNotes = async (ticket) => {
    setCallNoteTicket(ticket);
    setCallNoteForm({ summary: '', note: '', call_status: 'Connected', next_follow_up_at: '' });
    try {
      const res = await fetch(`${API}/service-tickets/${ticket.ticket_id}/call-notes`, { headers: getAuthHeaders() });
      if (res.ok) setCallNotes((await res.json()).call_notes || []);
      else { setCallNotes([]); toast.error('Call history could not be loaded'); }
    } catch { setCallNotes([]); toast.error('Network error loading call history'); }
  };
  const saveCallNote = async () => {
    if (!callNoteTicket || !callNoteForm.note.trim()) { toast.error('ပြောဆိုချက်ကို မဖြစ်မနေ မှတ်တမ်းတင်ပါ'); return; }
    setCallNoteSaving(true);
    try {
      const res = await fetch(`${API}/service-tickets/${callNoteTicket.ticket_id}/call-notes`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(callNoteForm) });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Call note save failed'); return; }
      setCallNotes(prev => [data, ...prev]);
      setCallNoteForm({ summary: '', note: '', call_status: 'Connected', next_follow_up_at: '' });
      toast.success('Ticket ထဲမှာ call note သိမ်းပြီးပါပြီ');
    } catch { toast.error('Network error saving call note'); }
    finally { setCallNoteSaving(false); }
  };
  const submitTransfer = async () => {
    if (!transferTicket) return;
    setTransferring(true);
    try {
      const res = await fetch(`${API}/service-tickets/${transferTicket.ticket_id}/transfer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ route_to: transferForm.route_to, noc_note: transferForm.noc_note, from_dept: 'NOC' })
      });
      if (res.ok) {
        toast.success(`Ticket transferred to ${transferForm.route_to.toUpperCase()} successfully`);
        setTransferTicket(null);
        setTransferForm({ route_to: 'cs', noc_note: '' });
        load();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Transfer failed');
      }
    } catch (e) { toast.error('Network error'); }
    finally { setTransferring(false); }
  };

  // DMA customer search
  const handleCustSearch = (val) => {
    setCustSearch(val);
    setSelectedCust(null);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (val.length < 2) { setCustResults([]); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/search?q=${encodeURIComponent(val)}`);
        if (res.ok) { const _d = await res.json(); setCustResults(Array.isArray(_d) ? _d : []); };
      } catch {}
      finally { setSearching(false); }
    }, 300);
  };

  const selectCustomer = (cust) => {
    setSelectedCust(cust);
    setCustResults([]);
    setCustSearch('');
  };

  const loadAvailability = async (date) => {
    setForm(f => ({ ...f, service_date: date }));
    try {
      const r = await fetch(`${API}/service-tickets/schedule/availability?date=${date}`);
      if (r.ok) setAvailability(await r.json());
    } catch {}
  };

  const handleIssueChange = (issue) => {
    const autoTeam = ISSUE_TEAM_MAP[issue] || '';
    setForm(f => ({ ...f, issue_type: issue, assign_team: autoTeam }));
  };

  const createTicket = async (e) => {
    e.preventDefault();
    if (!selectedCust) { toast.error('Please search and select a customer first'); return; }
    if (!form.issue_type) { toast.error('Select an issue category'); return; }
    if (!form.assign_team) { toast.error('Select a team to assign'); return; }
    try {
      const payload = {
        customer_id: selectedCust.username,
        customer_name: `${selectedCust.firstname || ''} ${selectedCust.lastname || ''}`.trim() || selectedCust.username,
        customer_phone: selectedCust.phone || selectedCust.mobile || '',
        address: selectedCust.address || '',
        township: selectedCust.city || '',
        service_type: selectedCust.service_name || '',
        issue_type: form.issue_type,
        priority: form.priority,
        description: form.description,
        assign_team: form.assign_team,
        service_date: form.service_date || (new Date(Date.now() + 86400000)).toISOString().split('T')[0],
        service_time: form.service_time,
      };
      const res = await fetch(`${API}/service-tickets`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      const teamLabel = TEAMS.find(t => t.value === form.assign_team)?.label || form.assign_team;
      toast.success(`${data.ticket_id} created!\nTeam: ${data.assigned_name || teamLabel}\nDate: ${data.service_date}`);
      setSelectedCust(null); setForm({ issue_type: '', priority: 'Medium', description: '', assign_team: '', service_date: '', service_time: '' }); setTab('tickets'); load();
    } catch { toast.error('Failed to create ticket'); }
  };

  const updateStatus = async (id, status) => {
    if (status === 'Scheduled') {
      // Open the schedule date/time picker instead of immediately saving
      setScheduleTicketId(id);
      setScheduleDateTime('');
      setScheduleTime('');
      return;
    }
    await fetch(`${API}/service-tickets/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
    toast.success('Status updated'); load();
  };

  const confirmSchedule = async () => {
    if (!scheduleDateTime) { toast.error('Please select a date'); return; }
    const dt = scheduleTime ? `${scheduleDateTime}T${scheduleTime}:00` : `${scheduleDateTime}T00:00:00`;
    await fetch(`${API}/service-tickets/${scheduleTicketId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'Scheduled', scheduled_datetime: dt })
    });
    toast.success('Ticket scheduled ✓');
    setScheduleTicketId(null);
    load();
  };

  const fmtScheduled = (dt) => {
    if (!dt) return null;
    const d = new Date(dt);
    if (isNaN(d)) return dt;
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const filteredTickets = (() => {
    let list = tickets;
    // Apply stat card filter first
    if (statFilter === 'in_progress') list = list.filter(t => displayTicketStatus(t.status) === 'In Progress');
    else if (statFilter === 'critical') list = list.filter(t => t.priority === 'Critical');
    else if (statFilter === 'today') list = list.filter(t => { const today = new Date().toISOString().slice(0,10); return t.service_date === today || (t.created_at && t.created_at.startsWith(today)); });
    // Then apply status chip filter
    if (filter !== 'all') list = list.filter(t => displayTicketStatus(t.status) === filter);
    if (searchQ) list = list.filter(t => t.customer_name?.toLowerCase().includes(searchQ.toLowerCase()) || t.ticket_id?.toLowerCase().includes(searchQ.toLowerCase()) || t.customer_phone?.includes(searchQ) || t.customer_id?.toLowerCase().includes(searchQ.toLowerCase()));
    if (deptFilter) list = list.filter(t => t.target_department === deptFilter || t.routed_to === deptFilter || t.current_department === deptFilter);
    // Filter by schedule date
    if (scheduleDateFilter) list = list.filter(t => t.scheduled_datetime && t.scheduled_datetime.startsWith(scheduleDateFilter));
    // Sort by scheduled_datetime when Scheduled filter is active
    if (filter === 'Scheduled' || sortBySchedule) {
      list = [...list].sort((a, b) => {
        const da = a.scheduled_datetime ? new Date(a.scheduled_datetime) : new Date('9999');
        const db = b.scheduled_datetime ? new Date(b.scheduled_datetime) : new Date('9999');
        return da - db;
      });
    }
    return list;
  })();

  const DEPT_OPTIONS = [
    { value: 'cs', label: 'NOC to CS', color: '#1565C0' },
    { value: 'infra', label: 'NOC to Infra', color: '#6A1B9A' },
    { value: 'bill', label: 'NOC to Bill', color: '#E65100' },
    { value: 'service', label: 'NOC to Service', color: '#1B5E20' },
  ];
  return (
    <div className="space-y-4" data-testid="operator-page">
      {/* ===== SCHEDULE DATE/TIME PICKER MODAL ===== */}
      {scheduleTicketId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) setScheduleTicketId(null); }}>
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-base font-bold text-[var(--md-on-surface)] mb-1">📅 Set Schedule Date & Time</h3>
            <p className="text-xs text-[var(--md-on-surface-variant)] mb-4">Ticket: <strong>{scheduleTicketId}</strong></p>
            <div className="space-y-3">
              <div>
                <label className="text-[12px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Visit Date *</label>
                <input type="date" value={scheduleDateTime} onChange={e => setScheduleDateTime(e.target.value)} className="md3-input h-11 w-full" min={new Date().toISOString().slice(0,10)} />
              </div>
              <div>
                <label className="text-[12px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">Time (optional)</label>
                <input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} className="md3-input h-11 w-full" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setScheduleTicketId(null)} className="md3-outlined-btn flex-1 py-2.5 text-sm">Cancel</button>
              <button onClick={confirmSchedule} disabled={!scheduleDateTime} className="md3-filled-btn flex-1 py-2.5 text-sm disabled:opacity-50">Confirm Schedule</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== OPERATOR TICKET CALL HISTORY MODAL ===== */}
      {callNoteTicket && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) setCallNoteTicket(null); }}>
          <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between border-b pb-3 mb-3">
              <div><h3 className="text-base font-bold">☎ Ticket Call History</h3><p className="text-xs text-[var(--md-on-surface-variant)]">{callNoteTicket.ticket_id} · {callNoteTicket.customer_name} · {callNoteTicket.customer_phone}</p></div>
              <button onClick={() => setCallNoteTicket(null)} className="text-xl font-bold">✕</button>
            </div>
            <div className="rounded-xl bg-[#E3F2FD] p-3 mb-4 text-xs text-[#0D47A1]">Call တစ်ကြိမ်ပြီးတိုင်း note အသစ်ထည့်ပါ။ အချိန်နှင့် Operator အမည်ကို system က အလိုအလျောက်သိမ်းပြီး အရင်မှတ်တမ်းများကို ပြင်/ဖျက်၍မရပါ။</div>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <input className="md3-input h-10 text-xs" placeholder="အကျဉ်းချုပ် (ဥပမာ: customer ပြန်ဆက်)" value={callNoteForm.summary} onChange={e => setCallNoteForm(f => ({...f, summary:e.target.value}))} />
              <select className="md3-input h-10 text-xs" value={callNoteForm.call_status} onChange={e => setCallNoteForm(f => ({...f, call_status:e.target.value}))}><option>Connected</option><option>No Answer</option><option>Busy</option><option>Callback Requested</option></select>
              <textarea className="md3-input text-xs sm:col-span-2" rows={3} required placeholder="ဘယ်အချိန် ဆက်သွယ်ပြီး ဘာပြောခဲ့သည်၊ customer ပြောထားသည်ကို ရေးပါ *" value={callNoteForm.note} onChange={e => setCallNoteForm(f => ({...f, note:e.target.value}))} />
              <label className="text-xs">နောက်တစ်ကြိမ် Follow-up အချိန် (optional)<input type="datetime-local" className="md3-input h-10 text-xs w-full mt-1" value={callNoteForm.next_follow_up_at} onChange={e => setCallNoteForm(f => ({...f, next_follow_up_at:e.target.value}))} /></label>
              <button onClick={saveCallNote} disabled={callNoteSaving || !callNoteForm.note.trim()} className="md3-filled-btn h-10 text-xs self-end disabled:opacity-50">{callNoteSaving ? 'သိမ်းနေသည်...' : '＋ Call Note သိမ်းမည်'}</button>
            </div>
            <div className="space-y-2"><h4 className="text-xs font-semibold">ယခင် Call မှတ်တမ်း ({callNotes.length})</h4>{callNotes.length === 0 ? <p className="text-xs text-[var(--md-on-surface-variant)] py-4 text-center">မှတ်တမ်းမရှိသေးပါ</p> : callNotes.map(n => <div key={n.note_id} className="rounded-xl border p-3 text-xs"><div className="flex flex-wrap gap-2 items-center mb-1"><strong>{n.operator_name || 'Operator'}</strong><span className="text-[var(--md-on-surface-variant)]">{n.called_at ? new Date(n.called_at).toLocaleString() : ''}</span><span className="md3-chip bg-[var(--md-surface-2)]">{n.call_status}</span></div><p className="font-medium">{n.summary || '—'}</p><p className="whitespace-pre-wrap mt-1">{n.note}</p>{n.next_follow_up_at && <p className="text-[#1565C0] mt-1">Next follow-up: {new Date(n.next_follow_up_at).toLocaleString()}</p>}</div>)}</div>
          </div>
        </div>
      )}
      {/* ===== NOC TICKET TRANSFER MODAL ===== */}
      {transferTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-[var(--md-outline-variant)] flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-[var(--md-on-surface)]">⇄ NOC Ticket Transfer</h2>
                <p className="text-[11px] text-[var(--md-on-surface-variant)] mt-0.5">Route ticket to another department</p>
              </div>
              <button onClick={() => setTransferTicket(null)} className="text-[var(--md-on-surface-variant)] hover:text-[var(--md-on-surface)] text-xl font-bold leading-none">✕</button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {/* Ticket Info */}
              <div className="bg-[var(--md-surface-1)] rounded-xl p-3 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold bg-[#E3F2FD] text-[#1565C0] px-2 py-0.5 rounded font-mono border border-[#90CAF9]">{transferTicket.customer_id || transferTicket.ticket_id}</span>
                  <span className="text-sm font-medium">{transferTicket.customer_name}</span>
                </div>
                <p className="text-xs text-[var(--md-on-surface-variant)]">Issue: <strong>{transferTicket.issue_type}</strong></p>
                <p className="text-xs text-[var(--md-on-surface-variant)]">Current Dept: <strong>{transferTicket.target_department || transferTicket.current_department || 'NOC'}</strong></p>
                <p className="text-xs text-[var(--md-on-surface-variant)]">Status: <strong>{transferTicket.status}</strong></p>
              </div>
              {/* Route To */}
              <div>
                <label className="text-[11px] font-semibold mb-2 block text-[var(--md-on-surface)]">Route To *</label>
                <div className="grid grid-cols-2 gap-2">
                  {DEPT_OPTIONS.map(d => (
                    <button
                      key={d.value}
                      onClick={() => setTransferForm(f => ({...f, route_to: d.value}))}
                      className="px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all"
                      style={transferForm.route_to === d.value
                        ? { borderColor: d.color, background: d.color + '15', color: d.color }
                        : { borderColor: '#E0E0E0', color: '#555' }}
                    >{d.label}</button>
                  ))}
                </div>
              </div>
              {/* NOC Note */}
              <div>
                <label className="text-[11px] font-semibold mb-1 block text-[var(--md-on-surface)]">NOC Note</label>
                <textarea
                  value={transferForm.noc_note}
                  onChange={e => setTransferForm(f => ({...f, noc_note: e.target.value}))}
                  placeholder="Add note for the receiving department..."
                  rows={3}
                  className="md3-input w-full text-xs resize-none"
                />
              </div>
              {/* Transfer History */}
              {transferTicket.transfer_history?.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold mb-1 text-[var(--md-on-surface-variant)]">Transfer History</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {transferTicket.transfer_history.map((h, i) => (
                      <div key={i} className="text-[10px] bg-[var(--md-surface-1)] rounded-lg px-2 py-1 flex items-center gap-2">
                        <span className="font-bold">{h.from_dept}→{h.to_dept}</span>
                        <span className="text-[var(--md-on-surface-variant)]">{h.transferred_by}</span>
                        <span className="text-[var(--md-on-surface-variant)] ml-auto">{h.transferred_at ? new Date(h.transferred_at).toLocaleDateString() : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-[var(--md-outline-variant)] flex gap-2 justify-end">
              <button onClick={() => setTransferTicket(null)} className="px-4 py-2 rounded-xl border border-[var(--md-outline-variant)] text-sm text-[var(--md-on-surface-variant)] hover:bg-[var(--md-surface-1)]">Cancel</button>
              <button
                onClick={submitTransfer}
                disabled={transferring}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                style={{ background: DEPT_OPTIONS.find(d => d.value === transferForm.route_to)?.color || '#7C4DFF' }}
              >{transferring ? 'Transferring...' : `⇄ Transfer to ${transferForm.route_to.toUpperCase()}`}</button>
            </div>
          </div>
        </div>
      )}
      {/* ===== NOC TICKET TRANSFER MODAL ===== */}
      {transferTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-[var(--md-outline-variant)] flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-[var(--md-on-surface)]">⇄ NOC Ticket Transfer</h2>
                <p className="text-[11px] text-[var(--md-on-surface-variant)] mt-0.5">Route ticket to another department</p>
              </div>
              <button onClick={() => setTransferTicket(null)} className="text-[var(--md-on-surface-variant)] hover:text-[var(--md-on-surface)] text-xl font-bold leading-none">✕</button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {/* Ticket Info */}
              <div className="bg-[var(--md-surface-1)] rounded-xl p-3 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold bg-[#E3F2FD] text-[#1565C0] px-2 py-0.5 rounded font-mono border border-[#90CAF9]">{transferTicket.customer_id || transferTicket.ticket_id}</span>
                  <span className="text-sm font-medium">{transferTicket.customer_name}</span>
                </div>
                <p className="text-xs text-[var(--md-on-surface-variant)]">Issue: <strong>{transferTicket.issue_type}</strong></p>
                <p className="text-xs text-[var(--md-on-surface-variant)]">Current Dept: <strong>{transferTicket.target_department || transferTicket.current_department || 'NOC'}</strong></p>
                <p className="text-xs text-[var(--md-on-surface-variant)]">Status: <strong>{transferTicket.status}</strong></p>
              </div>
              {/* Route To */}
              <div>
                <label className="text-[11px] font-semibold mb-2 block text-[var(--md-on-surface)]">Route To *</label>
                <div className="grid grid-cols-2 gap-2">
                  {DEPT_OPTIONS.map(d => (
                    <button
                      key={d.value}
                      onClick={() => setTransferForm(f => ({...f, route_to: d.value}))}
                      className="px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all"
                      style={transferForm.route_to === d.value
                        ? { borderColor: d.color, background: d.color + '15', color: d.color }
                        : { borderColor: '#E0E0E0', color: '#555' }}
                    >{d.label}</button>
                  ))}
                </div>
              </div>
              {/* NOC Note */}
              <div>
                <label className="text-[11px] font-semibold mb-1 block text-[var(--md-on-surface)]">NOC Note</label>
                <textarea
                  value={transferForm.noc_note}
                  onChange={e => setTransferForm(f => ({...f, noc_note: e.target.value}))}
                  placeholder="Add note for the receiving department..."
                  rows={3}
                  className="md3-input w-full text-xs resize-none"
                />
              </div>
              {/* Transfer History */}
              {transferTicket.transfer_history?.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold mb-1 text-[var(--md-on-surface-variant)]">Transfer History</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {transferTicket.transfer_history.map((h, i) => (
                      <div key={i} className="text-[10px] bg-[var(--md-surface-1)] rounded-lg px-2 py-1 flex items-center gap-2">
                        <span className="font-bold">{h.from_dept}→{h.to_dept}</span>
                        <span className="text-[var(--md-on-surface-variant)]">{h.transferred_by}</span>
                        <span className="text-[var(--md-on-surface-variant)] ml-auto">{h.transferred_at ? new Date(h.transferred_at).toLocaleDateString() : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-[var(--md-outline-variant)] flex gap-2 justify-end">
              <button onClick={() => setTransferTicket(null)} className="px-4 py-2 rounded-xl border border-[var(--md-outline-variant)] text-sm text-[var(--md-on-surface-variant)] hover:bg-[var(--md-surface-1)]">Cancel</button>
              <button
                onClick={submitTransfer}
                disabled={transferring}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                style={{ background: DEPT_OPTIONS.find(d => d.value === transferForm.route_to)?.color || '#7C4DFF' }}
              >{transferring ? 'Transferring...' : `⇄ Transfer to ${transferForm.route_to.toUpperCase()}`}</button>
            </div>
          </div>
        </div>
      )}
      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Tickets', value: stats.total || tickets.length, color: 'var(--md-primary)', filterKey: 'total' },
          { label: 'In Progress', value: stats.in_progress || 0, color: '#7C4DFF', filterKey: 'in_progress' },
          { label: 'Today', value: `${stats.today_completed || 0}/${stats.today_jobs || 0}`, color: '#006D3B', filterKey: 'today' },
          { label: 'Critical', value: stats.critical_open || 0, color: (stats.critical_open || 0) > 0 ? '#BA1A1A' : '#006D3B', filterKey: 'critical' },
        ].map((s, i) => (
          <div key={i} onClick={() => { if (s.filterKey === 'total') { setStatFilter(null); } else { setStatFilter(statFilter === s.filterKey ? null : s.filterKey); } setTab('tickets'); }} className={`bg-white rounded-xl border p-3 shadow-sm cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statFilter === s.filterKey ? 'ring-2 ring-offset-1' : 'border-[var(--md-outline-variant)]'}`} style={statFilter === s.filterKey ? { borderColor: s.color, boxShadow: `0 0 0 2px ${typeof s.color === 'string' && s.color.startsWith('#') ? s.color + '33' : 'rgba(0,82,204,0.2)'}` } : {}}>
            <p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">{s.label}</p>
            <p className="text-2xl font-bold tabular-nums" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      <section className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-3 shadow-sm" aria-label="Ticket lifecycle status overview">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div><h2 className="text-sm font-semibold text-[var(--md-on-surface)]">Ticket Status Overview</h2><p className="text-[11px] text-[var(--md-on-surface-variant)]">Live counts across all service tickets. Select a status to filter the list.</p></div>
          <button onClick={() => { setFilter('all'); setStatFilter(null); setTab('tickets'); }} className={`md3-chip text-[11px] cursor-pointer ${filter === 'all' && !statFilter ? 'bg-[var(--md-primary)] text-white' : 'bg-[var(--md-surface-2)]'}`}>All · {tickets.length}</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {STATUSES.map(status => (
            <button key={status} onClick={() => { setFilter(status); setStatFilter(null); setTab('tickets'); }} className={`rounded-lg px-2.5 py-2 text-left border transition-all hover:shadow-sm ${STATUS_CLR[status]} ${filter === status ? 'ring-2 ring-[var(--md-primary)] ring-offset-1' : 'border-transparent'}`}>
              <span className="block text-[10px] font-medium truncate">{status}</span>
              <span className="block text-xl font-bold tabular-nums leading-tight">{statusCounts[status] || 0}</span>
            </button>
          ))}
        </div>
      </section>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[var(--md-outline-variant)] rounded-xl p-1 flex-wrap">
          <TabsTrigger value="tickets" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">All Tickets</TabsTrigger>
          <TabsTrigger value="wifi-report" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]"><Headset size={14} className="mr-1 inline" />Wi-Fi Report</TabsTrigger>
          <TabsTrigger value="infradown" className="rounded-lg data-[state=active]:bg-[#FFEBEE] data-[state=active]:text-[#B71C1C]"><Warning size={14} className="mr-1 inline text-[#B71C1C]" />Infra Down ({infraDown.active_tasks?.reduce((sum, t) => sum + (t.impacted_count || 0), 0) || 0} users)</TabsTrigger>
          <TabsTrigger value="create" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">Create Support Ticket</TabsTrigger>
          <TabsTrigger value="pppoe-history" className="rounded-lg data-[state=active]:bg-[#E3F2FD] data-[state=active]:text-[#0D47A1]">☎ PPPoE Call History</TabsTrigger>
          <TabsTrigger value="external-history" className="rounded-lg data-[state=active]:bg-[#E8F5E9] data-[state=active]:text-[#1B5E20]">External Operator History ({externalHistory.length})</TabsTrigger>
          <TabsTrigger value="box-down-review" className="rounded-lg data-[state=active]:bg-[#FFF3E0] data-[state=active]:text-[#E65100]">BOX Down Review ({boxDownReview.length})</TabsTrigger>
          <TabsTrigger value="schedule" className="rounded-lg data-[state=active]:bg-[#E0F2F1] data-[state=active]:text-[#00695C]"><CalendarCheck size={14} className="mr-1 inline" />Auto Schedule</TabsTrigger>
        </TabsList>

        {/* ===== ALL TICKETS ===== */}
        <TabsContent value="tickets" className="space-y-3 mt-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            {statFilter && <div className="w-full flex items-center gap-2 mb-1"><span className="text-xs text-[var(--md-on-surface-variant)]">Filtered by: <strong className="text-[var(--md-primary)]">{statFilter === 'in_progress' ? 'In Progress' : statFilter === 'critical' ? 'Critical' : statFilter === 'today' ? 'Today' : statFilter}</strong></span><button onClick={() => setStatFilter(null)} className="text-xs text-[var(--md-primary)] underline hover:no-underline">Clear filter</button></div>}
            <div className="flex flex-wrap gap-1.5">
              {['all', ...STATUSES].map(s => (
                <button key={s} onClick={() => { setFilter(s); setStatFilter(null); }} className={`md3-chip text-[11px] cursor-pointer ${filter === s ? 'bg-[var(--md-primary)] text-white' : 'bg-[var(--md-surface-2)]'}`}>{s === 'all' ? `All (${tickets.length})` : `${s} (${statusCounts[s] || 0})`}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <span className="text-[10px] text-[var(--md-on-surface-variant)] self-center">Dept:</span>
              {[{key:'CS',label:'CS',color:'#1565C0'},{key:'Infra',label:'Infra',color:'#6A1B9A'},{key:'Bill',label:'Bill',color:'#E65100'},{key:'Service',label:'Service',color:'#1B5E20'}].map(d => (
                <button key={d.key} onClick={() => setDeptFilter(deptFilter === d.key ? null : d.key)} className="md3-chip text-[11px] cursor-pointer" style={deptFilter === d.key ? {background: d.color, color: '#fff'} : {}}>{d.label}</button>
              ))}
              {deptFilter && <button onClick={() => setDeptFilter(null)} className="md3-chip text-[11px] cursor-pointer bg-[#FFEBEE] text-[#B71C1C]">✕ Clear</button>}
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <span className="text-[10px] text-[var(--md-on-surface-variant)] self-center">Schedule Date:</span>
              <input type="date" value={scheduleDateFilter} onChange={e => setScheduleDateFilter(e.target.value)} className="md3-input h-7 text-xs w-36" />
              {scheduleDateFilter && <button onClick={() => setScheduleDateFilter('')} className="md3-chip text-[11px] bg-[#FFEBEE] text-[#B71C1C] cursor-pointer">✕ Clear</button>}
              <button onClick={() => setSortBySchedule(s => !s)} className={`md3-chip text-[11px] cursor-pointer ${sortBySchedule ? 'bg-[var(--md-primary)] text-white' : 'bg-[var(--md-surface-2)]'}`}>⇅ Sort by Schedule</button>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative"><MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--md-outline)]" /><input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search tickets..." className="md3-input pl-8 h-9 w-48 text-xs" /></div>
              <button onClick={() => setTab('create')} className="md3-filled-btn text-xs flex items-center gap-1.5" data-testid="create-ticket-btn"><Plus size={14} /> Create Ticket</button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] overflow-hidden shadow-sm">
            <table className="w-full" data-testid="tickets-table">
              <thead><tr className="bg-[var(--md-surface-1)]">
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">PPPoE ID</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Customer Name</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden md:table-cell">Issue</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Priority</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Status</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden lg:table-cell">Team</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden lg:table-cell">Dept/Route</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden xl:table-cell cursor-pointer select-none" onClick={() => setSortBySchedule(s => !s)}>
                  <span className="flex items-center gap-1">📅 Scheduled Date {sortBySchedule ? '▲' : '⇅'}</span>
                </th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Action</th>
              </tr></thead>
              <tbody>
                {filteredTickets.length === 0 ? <tr><td colSpan={7} className="text-center py-10 text-sm text-[var(--md-on-surface-variant)]">No tickets found</td></tr> :
                filteredTickets.map(t => (
                  <tr key={t.ticket_id} className="border-t border-[var(--md-outline-variant)] md3-table-row">
                    <td className="px-4 py-3">{t.customer_id ? <span className="text-[11px] font-bold bg-[#E3F2FD] text-[#1565C0] px-2 py-0.5 rounded font-mono border border-[#90CAF9]">{t.customer_id}</span> : <span className="text-xs text-[var(--md-on-surface-variant)]">{t.ticket_id}</span>}</td>
                    <td className="px-4 py-3"><p className="text-sm font-medium">{t.customer_name}</p><p className="text-[11px] text-[var(--md-on-surface-variant)]">{t.customer_phone}</p></td>
                    <td className="px-4 py-3 hidden md:table-cell"><span className="md3-chip text-[10px] bg-[var(--md-surface-2)]">{t.issue_type}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs font-bold ${PRI_CLR[t.priority] || ''}`}>{t.priority}</span></td>
                    <td className="px-4 py-3">
                      <Select value={t.status} onValueChange={v => updateStatus(t.ticket_id, v)}>
                        <SelectTrigger className={`h-7 text-[11px] w-28 border-0 rounded-lg ${STATUS_CLR[t.status] || ''}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell"><span className="text-xs">{t.assigned_name || t.target_department || <span className="italic text-[var(--md-on-surface-variant)]">Unassigned</span>}</span></td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {t.routed_to ? (
                        <div>
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{background:'#E8EAF6',color:'#283593'}}>NOC→{t.routed_to}</span>
                          {t.routed_at && <p className="text-[10px] text-[var(--md-on-surface-variant)] mt-0.5">{new Date(t.routed_at).toLocaleDateString()}</p>}
                        </div>
                      ) : <span className="text-[10px] text-[var(--md-on-surface-variant)]">—</span>}
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      {t.service_date || t.scheduled_datetime ? (
                        <div className="flex flex-col gap-0.5">
                          {t.schedule_mode === 'manual' ? (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FFEBEE] text-[#B71C1C] whitespace-nowrap">Manual · {(t.service_date || t.scheduled_datetime || '').slice(0,10)}</span>
                          ) : (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#E0F2F1] text-[#00695C] whitespace-nowrap">Auto · {(t.service_date || t.scheduled_datetime || '').slice(0,10)}</span>
                          )}
                          {t.assigned_name && <span className="text-[10px] text-[var(--md-on-surface-variant)]">{t.assigned_name}</span>}
                          {MANUAL_PRIORITIES.includes(t.priority) && (
                            <button onClick={() => { setScheduleTicketId(t.ticket_id); setScheduleDateTime((t.service_date || t.scheduled_datetime || '').slice(0,10)); setScheduleTime(t.scheduled_datetime ? t.scheduled_datetime.slice(11,16) : ''); }} className="text-[10px] text-[var(--md-primary)] underline text-left">Edit</button>
                          )}
                        </div>
                      ) : (
                        t.status === 'Scheduled' && MANUAL_PRIORITIES.includes(t.priority) ? (
                          <button onClick={() => { setScheduleTicketId(t.ticket_id); setScheduleDateTime(''); setScheduleTime(''); }} className="text-[11px] text-[#B71C1C] bg-[#FFEBEE] px-2 py-0.5 rounded-lg whitespace-nowrap">⚠ Set Date</button>
                        ) : <span className="text-[10px] text-[var(--md-on-surface-variant)]">Pending auto-assign</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => openCallNotes(t)} className="text-[11px] font-medium px-2.5 py-1 rounded-lg border border-[#1565C0] text-[#1565C0] hover:bg-[#E3F2FD] transition-colors whitespace-nowrap mr-1" title="View and add call history">☎ Calls{t.call_notes?.length ? ` (${t.call_notes.length})` : ''}</button>
                      <button
                        onClick={() => { setTransferTicket(t); setTransferForm({ route_to: 'cs', noc_note: '' }); }}
                        className="text-[11px] font-medium px-2.5 py-1 rounded-lg border border-[#7C4DFF] text-[#7C4DFF] hover:bg-[#EDE7F6] transition-colors whitespace-nowrap"
                        title="Transfer to department"
                      >⇄ Transfer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ===== PPPoE CALL HISTORY ===== */}
        <TabsContent value="pppoe-history" className="mt-4"><PppoeCallHistoryPanel /></TabsContent>
        {/* ===== CALL CENTER WI-FI REPORT ===== */}
        <TabsContent value="wifi-report" className="mt-4">
          <CallCenterWifiReport embedded />
        </TabsContent>

        {/* ===== INFRA DOWN IMPACTED CUSTOMERS ===== */}
        <TabsContent value="infradown" className="space-y-4 mt-4">
          {infraDown.active_tasks?.length === 0 && infraDown.ongoing_issues?.length === 0 ? (
            <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-8 text-center">
              <ShieldCheck size={40} className="mx-auto mb-3 text-[#006D3B]" />
              <p className="text-sm font-medium text-[#006D3B]">All Clear</p>
              <p className="text-xs text-[var(--md-on-surface-variant)] mt-1">No active infrastructure issues affecting customers</p>
            </div>
          ) : (
            <>
              {/* Active BMFT Tasks with Impacted Users */}
              {infraDown.active_tasks?.map(task => (
                <div key={task.task_id} className="bg-white rounded-xl border border-[var(--md-outline-variant)] shadow-sm overflow-hidden">
                  <div className={`px-4 py-3 flex items-center justify-between ${task.priority === 'Critical' ? 'bg-[#FFEBEE] border-b border-[#FFCDD2]' : 'bg-[#FFF8E1] border-b border-[#FFE082]'}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Warning size={16} weight="fill" className={task.priority === 'Critical' ? 'text-[#B71C1C]' : 'text-[#F57F17]'} />
                      <span className="text-xs font-bold text-[var(--md-primary)] tabular-nums">{task.task_id}</span>
                      <span className="md3-chip text-[10px] bg-white/70">{task.issue_type}</span>
                      <span className={`md3-chip text-[10px] ${task.status === 'In Progress' ? 'bg-[#E8EAF6] text-[#283593]' : 'bg-[#FFF8E1] text-[#F57F17]'}`}>{task.status}</span>
                      {task.priority === 'Critical' && <span className="md3-chip text-[10px] bg-[#FFEBEE] text-[#B71C1C]">CRITICAL</span>}
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-[var(--md-on-surface-variant)]">Link: <strong>{task.link_name}</strong></p>
                      {task.down_time && <p className="text-[10px] text-[var(--md-on-surface-variant)]">Down: {new Date(task.down_time).toLocaleString()}</p>}
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-[var(--md-surface-1)] border-b border-[var(--md-outline-variant)]">
                    <p className="text-xs font-medium"><Headset size={13} className="inline mr-1" />{task.impacted_count} Impacted Customer(s)</p>
                  </div>
                  <table className="w-full">
                    <thead><tr className="bg-[var(--md-surface-1)]">
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">User ID</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Name</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2 hidden md:table-cell">OLT</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2 hidden md:table-cell">Board/Port</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Status</th>
                    </tr></thead>
                    <tbody>
                      {task.customers?.map((c, ci) => (
                        <tr key={ci} className="border-t border-[var(--md-outline-variant)] hover:bg-[var(--md-surface-1)] transition-colors">
                          <td className="px-4 py-2"><span className="text-xs font-mono tabular-nums">{c.user_id}</span></td>
                          <td className="px-4 py-2"><span className="text-xs">{c.name || c.user_id}</span></td>
                          <td className="px-4 py-2 hidden md:table-cell"><span className="text-xs">{c.olt || '-'}</span></td>
                          <td className="px-4 py-2 hidden md:table-cell"><span className="text-xs tabular-nums">{c.board && c.port ? `${c.board}/${c.port}` : '-'}</span></td>
                          <td className="px-4 py-2"><span className={`md3-chip text-[10px] ${c.status === 'Active' || c.status === 'Online' ? 'bg-[#E8F5E9] text-[#1B5E20]' : c.status === 'Unknown' ? 'bg-[#FFF8E1] text-[#F57F17]' : 'bg-[#FFEBEE] text-[#B71C1C]'}`}>{c.status || 'Affected'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {/* Ongoing Link Down Issues */}
              {infraDown.ongoing_issues?.length > 0 && (
                <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] shadow-sm overflow-hidden">
                  <div className="px-4 py-3 bg-[#FFF3E0] border-b border-[#FFE0B2]">
                    <p className="text-xs font-medium flex items-center gap-1"><Path size={14} className="text-[#E65100]" /> Ongoing Link Down Issues ({infraDown.ongoing_issues.length})</p>
                  </div>
                  <table className="w-full">
                    <thead><tr className="bg-[var(--md-surface-1)]">
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Issue ID</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Link Name</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Error Type</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Down Since</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Days</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-2">Affected</th>
                    </tr></thead>
                    <tbody>
                      {infraDown.ongoing_issues.map((issue, ii) => (
                        <tr key={ii} className="border-t border-[var(--md-outline-variant)] hover:bg-[var(--md-surface-1)]">
                          <td className="px-4 py-2"><span className="text-xs font-mono">{issue.issue_id}</span></td>
                          <td className="px-4 py-2"><span className="text-xs font-medium">{issue.link_name}</span></td>
                          <td className="px-4 py-2"><span className="md3-chip text-[10px] bg-[#FFEBEE] text-[#B71C1C]">{issue.error_type}</span></td>
                          <td className="px-4 py-2"><span className="text-xs tabular-nums">{issue.down_time}</span></td>
                          <td className="px-4 py-2"><span className={`text-xs font-bold ${(issue.day_count || 0) > 30 ? 'text-[#B71C1C]' : 'text-[#F57F17]'}`}>{Math.round(issue.day_count || 0)}d</span></td>
                          <td className="px-4 py-2"><span className="text-xs font-bold text-[#B71C1C]">{issue.affected_users || 0} users</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </TabsContent>
        {/* ===== CREATE SUPPORT TICKET ===== */}
        <TabsContent value="schedule" className="mt-4 space-y-4">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-bold text-[var(--md-on-surface)]">Auto-Schedule Service Tickets</h2>
              <p className="text-xs text-[var(--md-on-surface-variant)] mt-0.5">Assign scheduled dates to unscheduled tickets — 8 tickets per team per day, ordered by priority</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => runAutoSchedule(true)}
                disabled={autoScheduleRunning}
                className="md3-outlined-btn text-xs flex items-center gap-1.5 disabled:opacity-50"
              >
                <Robot size={14} /> Preview (Dry Run)
              </button>
              <button
                onClick={() => runAutoSchedule(false)}
                disabled={autoScheduleRunning}
                className="md3-filled-btn text-xs flex items-center gap-1.5 disabled:opacity-50"
              >
                {autoScheduleRunning ? <ArrowsClockwise size={14} className="animate-spin" /> : <CalendarCheck size={14} />}
                {autoScheduleRunning ? 'Scheduling...' : 'Run Auto-Schedule'}
              </button>
            </div>
          </div>

          {/* Auto-schedule result */}
          {autoScheduleResult && (
            <div className={`rounded-xl p-4 text-sm ${autoScheduleResult.dry_run ? 'bg-[#FFF8E1] border border-[#FFE082]' : 'bg-[#E8F5E9] border border-[#A5D6A7]'}`}>
              <p className="font-semibold mb-2">{autoScheduleResult.dry_run ? '🔍 Preview Result' : '✅ Auto-Schedule Complete'}</p>
              <p className="text-xs">{autoScheduleResult.message}</p>
              {autoScheduleResult.assignments?.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                  {autoScheduleResult.assignments.slice(0, 20).map(a => (
                    <div key={a.ticket_id} className="flex items-center gap-2 text-[11px]">
                      <span className="font-mono bg-white/60 px-1.5 py-0.5 rounded">{a.ticket_id}</span>
                      <span className="text-[var(--md-on-surface-variant)]">{a.team}</span>
                      <span className="font-semibold text-[#00695C]">→ {a.scheduled_date}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${a.priority === 'Critical' ? 'bg-[#FFEBEE] text-[#B71C1C]' : a.priority === 'High' ? 'bg-[#FFF3E0] text-[#E65100]' : 'bg-[#E3F2FD] text-[#1565C0]'}`}>{a.priority}</span>
                    </div>
                  ))}
                  {autoScheduleResult.assignments.length > 20 && <p className="text-[11px] text-[var(--md-on-surface-variant)]">...and {autoScheduleResult.assignments.length - 20} more</p>}
                </div>
              )}
            </div>
          )}

          {/* Calendar view controls */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <CalendarBlank size={16} className="text-[var(--md-on-surface-variant)]" />
              <label className="text-xs font-medium text-[var(--md-on-surface-variant)]">Week starting:</label>
              <input type="date" value={scheduleViewDate} onChange={e => { setScheduleViewDate(e.target.value); loadTeamSchedule(e.target.value); }} className="md3-input h-8 text-xs w-36" />
            </div>
            <button onClick={() => loadTeamSchedule(scheduleViewDate)} className="md3-chip text-xs cursor-pointer flex items-center gap-1"><ArrowsClockwise size={12} /> Refresh</button>
          </div>

          {/* Per-team calendar grid */}
          {teamSchedule.length === 0 ? (
            <div className="text-center py-12 text-sm text-[var(--md-on-surface-variant)]">
              <CalendarCheck size={40} className="mx-auto mb-3 opacity-30" />
              <p>No scheduled tickets found for this week.</p>
              <p className="text-xs mt-1">Run Auto-Schedule to assign dates to unscheduled tickets.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {teamSchedule.map(team => (
                <div key={team.team} className="bg-white rounded-xl border border-[var(--md-outline-variant)] shadow-sm overflow-hidden">
                  {/* Team header */}
                  <div className="px-4 py-3 bg-[var(--md-surface-1)] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[var(--md-primary)] text-white text-xs font-bold flex items-center justify-center">{team.team.charAt(0)}</div>
                      <div>
                        <p className="text-sm font-semibold">{team.team}</p>
                        <p className="text-[11px] text-[var(--md-on-surface-variant)]">{team.days.reduce((s,d) => s+d.count, 0)} tickets scheduled this week</p>
                      </div>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {team.days.map(d => (
                        <div key={d.date} className={`text-center px-2 py-1 rounded-lg text-[10px] font-medium ${d.full ? 'bg-[#FFEBEE] text-[#B71C1C]' : d.count >= 6 ? 'bg-[#FFF8E1] text-[#F57F17]' : 'bg-[#E8F5E9] text-[#1B5E20]'}`}>
                          <div>{new Date(d.date + 'T00:00:00').toLocaleDateString('en-GB', {day:'2-digit',month:'short'})}</div>
                          <div className="font-bold">{d.count}/{d.capacity}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Day columns */}
                  <div className="overflow-x-auto">
                    <div className="flex min-w-max">
                      {team.days.map(d => (
                        <div key={d.date} className="min-w-[160px] border-r border-[var(--md-outline-variant)] last:border-r-0">
                          <div className={`px-3 py-2 text-center text-[11px] font-semibold border-b border-[var(--md-outline-variant)] ${d.full ? 'bg-[#FFEBEE] text-[#B71C1C]' : 'bg-[var(--md-surface-2)]'}`}>
                            {new Date(d.date + 'T00:00:00').toLocaleDateString('en-GB', {weekday:'short', day:'2-digit', month:'short'})}
                            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${d.full ? 'bg-[#B71C1C] text-white' : 'bg-[var(--md-primary)] text-white'}`}>{d.count}/{d.capacity}</span>
                          </div>
                          <div className="p-2 space-y-1.5 min-h-[80px]">
                            {d.tickets.map(t => (
                              <div key={t.ticket_id} className="bg-[var(--md-surface-1)] rounded-lg px-2 py-1.5 text-[10px] border border-[var(--md-outline-variant)] hover:border-[var(--md-primary)] transition-colors cursor-pointer" onClick={() => { setScheduleTicketId(t.ticket_id); setScheduleDateTime(t.scheduled_datetime?.slice(0,10) || ''); setScheduleTime(t.scheduled_datetime?.slice(11,16) || ''); }}>
                                <div className="flex items-center justify-between gap-1 mb-0.5">
                                  <span className="font-mono text-[#1565C0] font-bold">{t.customer_id || t.ticket_id}</span>
                                  <span className={`px-1 py-0.5 rounded text-[9px] font-bold ${t.priority === 'Critical' ? 'bg-[#FFEBEE] text-[#B71C1C]' : t.priority === 'High' ? 'bg-[#FFF3E0] text-[#E65100]' : t.priority === 'Medium' ? 'bg-[#E3F2FD] text-[#1565C0]' : 'bg-[#F5F5F5] text-[#616161]'}`}>{t.priority}</span>
                                </div>
                                <p className="text-[var(--md-on-surface)] truncate">{t.customer_name || '—'}</p>
                                <p className="text-[var(--md-on-surface-variant)] truncate">{t.issue_type}</p>
                              </div>
                            ))}
                            {d.count === 0 && <p className="text-[10px] text-[var(--md-on-surface-variant)] text-center py-2 italic">No tickets</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="external-history" className="space-y-3 mt-4">
          <div className="flex items-center justify-between bg-[#E8F5E9] border border-[#A5D6A7] rounded-xl px-4 py-3"><div><p className="font-medium text-[#1B5E20]">Imported Operator History</p><p className="text-xs text-[#2E7D32]">MBOCSSDaily external records • matched history and review status</p></div><button onClick={loadExternalHistory} className="md3-outlined-btn text-xs">↻ Refresh</button></div>
          {externalLoading ? <div className="bg-white rounded-xl border p-8 text-center text-sm">Loading imported history...</div> : <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] overflow-auto shadow-sm"><table className="w-full text-xs"><thead><tr className="bg-[var(--md-surface-1)]"><th className="text-left px-3 py-3">Called At</th><th className="text-left px-3 py-3">Customer ID</th><th className="text-left px-3 py-3">Operator</th><th className="text-left px-3 py-3">Call Status</th><th className="text-left px-3 py-3">Summary</th><th className="text-left px-3 py-3">Conversation / Note</th><th className="text-left px-3 py-3">Match</th></tr></thead><tbody>{externalHistory.map((r,i)=><tr key={r.idempotency_key || i} className="border-t"><td className="px-3 py-2 whitespace-nowrap">{r.called_at ? new Date(r.called_at).toLocaleString() : '-'}</td><td className="px-3 py-2 font-mono">{r.customer_id || '-'}</td><td className="px-3 py-2">{r.operator_name || '-'}</td><td className="px-3 py-2"><span className="md3-chip bg-[var(--md-surface-2)]">{r.call_status || 'Imported'}</span></td><td className="px-3 py-2">{r.summary || '-'}</td><td className="px-3 py-2 min-w-[260px] whitespace-pre-wrap">{r.note || '-'}</td><td className="px-3 py-2"><span className={`md3-chip ${r.match_status === 'matched_unique' ? 'bg-[#E8F5E9] text-[#1B5E20]' : r.match_status === 'ambiguous' ? 'bg-[#FFF3E0] text-[#E65100]' : 'bg-[#ECEFF1]'}`}>{r.match_status || 'review'}</span></td></tr>)}</tbody></table>{externalHistory.length===0 && <p className="p-8 text-center text-sm text-[var(--md-on-surface-variant)]">No imported history found</p>}</div>}
        </TabsContent>
        <TabsContent value="box-down-review" className="space-y-3 mt-4">
          <div className="flex items-center justify-between bg-[#FFF3E0] border border-[#FFCC80] rounded-xl px-4 py-3"><div><p className="font-medium text-[#E65100]">BOX / FAT Down Review</p><p className="text-xs text-[#EF6C00]">Imported external records for NOC review</p></div><button onClick={loadExternalHistory} className="md3-outlined-btn text-xs">↻ Refresh</button></div>
          <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] overflow-auto shadow-sm"><table className="w-full text-xs"><thead><tr className="bg-[var(--md-surface-1)]"><th className="text-left px-3 py-3">Customer ID</th><th className="text-left px-3 py-3">FAT Name</th><th className="text-left px-3 py-3">Error Type</th><th className="text-left px-3 py-3">Remark</th><th className="text-left px-3 py-3">Up/Down</th></tr></thead><tbody>{boxDownReview.map((r,i)=><tr key={r.idempotency_key || i} className="border-t"><td className="px-3 py-2 font-mono">{r.customer_id || '-'}</td><td className="px-3 py-2">{r.fat_name || '-'}</td><td className="px-3 py-2 text-[#B71C1C]">{r.error_type || '-'}</td><td className="px-3 py-2">{r.note || '-'}</td><td className="px-3 py-2">{r.up_down || 'Review'}</td></tr>)}</tbody></table>{boxDownReview.length===0 && <p className="p-8 text-center text-sm text-[var(--md-on-surface-variant)]">No BOX-down review records found</p>}</div>
        </TabsContent>
        <TabsContent value="create" className="mt-4">
          <div className="max-w-3xl mx-auto space-y-5">
            {/* STEP 1: Customer Search */}
            <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-full bg-[var(--md-primary)] text-white text-xs font-bold flex items-center justify-center">1</div>
                <h3 className="text-[16px] font-medium">Search Customer</h3>
              </div>
              <p className="text-[12px] text-[var(--md-on-surface-variant)] mb-3">Search by Customer ID, Phone Number, Home Number, or PPPoE Username</p>
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--md-on-surface-variant)]" />
                <input value={custSearch} onChange={e => handleCustSearch(e.target.value)} placeholder="Type customer ID, phone, username to search..." className="md3-input pl-10 h-12 text-[15px]" data-testid="cust-search-input" autoFocus />
                {searching && <div className="absolute right-3.5 top-1/2 -translate-y-1/2"><div className="w-5 h-5 border-2 border-[var(--md-primary)] border-t-transparent rounded-full animate-spin" /></div>}
              </div>

              {/* Search Results */}
              {custResults.length > 0 && !selectedCust && (
                <div className="mt-3 border border-[var(--md-outline-variant)] rounded-xl overflow-hidden max-h-[300px] overflow-y-auto" data-testid="cust-search-results">
                  <div className="px-3 py-2 bg-[var(--md-surface-1)] text-[11px] text-[var(--md-on-surface-variant)] font-medium">{custResults.length} customer(s) found</div>
                  {custResults.map((c, i) => (
                    <button key={i} onClick={() => selectCustomer(c)} className="w-full text-left px-4 py-3 flex items-center gap-3 border-t border-[var(--md-outline-variant)] hover:bg-[var(--md-surface-1)] transition-colors">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${c.enableuser === 1 ? 'bg-[#E8F5E9]' : 'bg-[#FFEBEE]'}`}>
                        <User size={18} className={c.enableuser === 1 ? 'text-[#1B5E20]' : 'text-[#B71C1C]'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{c.firstname || ''} {c.lastname || ''} <span className="text-[var(--md-on-surface-variant)] font-normal">({c.username})</span></p>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-[var(--md-on-surface-variant)]">
                          {(c.phone || c.mobile) && <span className="flex items-center gap-1"><Phone size={10} />{c.phone || c.mobile}</span>}
                          {c.address && <span className="flex items-center gap-1"><MapPin size={10} />{c.address}</span>}
                          {c.service_name && <span className="flex items-center gap-1"><WifiHigh size={10} />{c.service_name}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`md3-chip text-[10px] ${c.enableuser === 1 ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-[#FFEBEE] text-[#B71C1C]'}`}>{c.enableuser === 1 ? 'Active' : 'Suspended'}</span>
                        <CaretRight size={14} className="text-[var(--md-outline)]" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* STEP 2: Customer Info Panel (shown after selection) */}
            {selectedCust && (
              <div className="bg-white rounded-xl border-2 border-[var(--md-primary)] p-5 shadow-sm" data-testid="selected-customer-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-[var(--md-success)] text-white flex items-center justify-center"><Check size={14} weight="bold" /></div>
                    <h3 className="text-[16px] font-medium">Customer Selected</h3>
                  </div>
                  <button onClick={() => setSelectedCust(null)} className="text-xs text-[var(--md-primary)] underline">Change</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <InfoCell label="Customer ID" value={selectedCust.username} />
                  <InfoCell label="Name" value={`${selectedCust.firstname || ''} ${selectedCust.lastname || ''}`.trim() || '-'} />
                  <InfoCell label="Phone" value={selectedCust.phone || selectedCust.mobile || '-'} />
                  <InfoCell label="Status" value={selectedCust.enableuser === 1 ? 'Active' : 'Suspended'} color={selectedCust.enableuser === 1 ? '#1B5E20' : '#B71C1C'} />
                  <InfoCell label="Address" value={selectedCust.address || '-'} />
                  <InfoCell label="Township" value={selectedCust.city || '-'} />
                  <InfoCell label="Service Package" value={selectedCust.service_name || '-'} />
                  <InfoCell label="MAC Address" value={selectedCust.mac || selectedCust.maccm || '-'} mono />
                </div>
              </div>
            )}

            {/* STEP 3: Ticket Details */}
            {selectedCust && (
              <form onSubmit={createTicket} className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-5 shadow-sm space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-full bg-[var(--md-primary)] text-white text-xs font-bold flex items-center justify-center">2</div>
                  <h3 className="text-[16px] font-medium">Ticket Details</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[12px] font-medium text-[var(--md-on-surface-variant)] mb-1.5 block">Issue Category *</label>
                    <Select value={form.issue_type} onValueChange={handleIssueChange}>
                      <SelectTrigger className="bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-11" data-testid="issue-select"><SelectValue placeholder="Select issue type" /></SelectTrigger>
                      <SelectContent className="bg-white">{ISSUES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[12px] font-medium text-[var(--md-on-surface-variant)] mb-1.5 block">Priority Level</label>
                    <Select value={form.priority} onValueChange={v => setForm({...form, priority: v, service_date: MANUAL_PRIORITIES.includes(v) ? form.service_date : '', assign_team: MANUAL_PRIORITIES.includes(v) ? form.assign_team : ''})}>
                      <SelectTrigger className="bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-11"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="Normal"><span className="text-[#0D47A1]">Normal — Auto-scheduled by system</span></SelectItem>
                        <SelectItem value="Urgent"><span className="text-[#B71C1C] font-bold">🚨 Urgent — Manual schedule required</span></SelectItem>
                        <SelectItem value="Require"><span className="text-[#E65100] font-bold">⚡ Require — Manual schedule required</span></SelectItem>
                      </SelectContent>
                    </Select>
                    {!MANUAL_PRIORITIES.includes(form.priority) && (
                      <p className="text-[11px] text-[#00695C] mt-1.5 flex items-center gap-1">✅ System will auto-assign team &amp; date (township-based grouping, 7 sites/day limit)</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-[12px] font-medium text-[var(--md-on-surface-variant)] mb-1.5 block">Problem Description</label>
                  <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="md3-input min-h-[80px] resize-none" placeholder="Describe the customer's problem in detail..." data-testid="desc-input" />
                </div>

                {/* Team Assignment - Step 3 — only for Urgent/Require */}
                {MANUAL_PRIORITIES.includes(form.priority) && (
                <div className="border border-[var(--md-outline-variant)] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-full bg-[var(--md-primary)] text-white text-[10px] font-bold flex items-center justify-center">3</div>
                    <label className="text-[14px] font-medium">Assign to Team (Manual Override)</label>
                  </div>

                  {/* Workflow explanation */}
                  {form.issue_type && ISSUE_TEAM_MAP[form.issue_type] ? (
                    <div className="flex items-center gap-2 mb-3 p-2.5 rounded-lg bg-[#E3F2FD]">
                      <Warning size={16} className="text-[#0D47A1] flex-shrink-0" />
                      <p className="text-[12px] text-[#0D47A1]">
                        <span className="font-bold">Auto-suggested:</span> "{form.issue_type}" issues are handled by <span className="font-bold">{TEAMS.find(t => t.value === ISSUE_TEAM_MAP[form.issue_type])?.label}</span>. You can override below.
                      </p>
                    </div>
                  ) : form.issue_type ? (
                    <p className="text-[12px] text-[var(--md-on-surface-variant)] mb-3">No auto-suggestion for "{form.issue_type}". Please select a team manually.</p>
                  ) : (
                    <p className="text-[12px] text-[var(--md-on-surface-variant)] mb-3">Select an issue category first to get a team suggestion, or choose manually.</p>
                  )}

                  {/* Team cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {TEAMS.map(t => {
                      const isSelected = form.assign_team === t.value;
                      const isSuggested = form.issue_type && ISSUE_TEAM_MAP[form.issue_type] === t.value;
                      return (
                        <button key={t.value} type="button" onClick={() => setForm({...form, assign_team: t.value})}
                          className={`relative p-4 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-[var(--md-primary)] bg-[var(--md-primary-container)] shadow-md' : 'border-[var(--md-outline-variant)] hover:bg-[var(--md-surface-1)] hover:border-[var(--md-outline)]'}`}>
                          {/* Suggested badge */}
                          {isSuggested && !isSelected && (
                            <span className="absolute -top-2 right-3 bg-[#E65100] text-white text-[9px] font-bold px-2 py-0.5 rounded-full">SUGGESTED</span>
                          )}
                          {isSelected && (
                            <span className="absolute -top-2 right-3 bg-[var(--md-primary)] text-white text-[9px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5"><Check size={10} weight="bold" /> SELECTED</span>
                          )}
                          <div className="flex items-center gap-2.5 mb-2">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isSelected ? 'bg-[var(--md-primary)] text-white' : 'bg-[var(--md-surface-2)]'}`}>
                              {t.value === 'IMFT' ? <Wrench size={18} weight={isSelected ? 'fill' : 'regular'} /> : t.value === 'BMFT' ? <Path size={18} weight={isSelected ? 'fill' : 'regular'} /> : <ShieldCheck size={18} weight={isSelected ? 'fill' : 'regular'} />}
                            </div>
                            <div>
                              <p className={`text-[13px] font-medium ${isSelected ? 'text-[var(--md-on-primary-container)]' : ''}`}>{t.label}</p>
                              <p className="text-[10px] text-[var(--md-on-surface-variant)]">{t.value === 'IMFT' ? 'Last-mile & customer site' : t.value === 'BMFT' ? 'Backbone fiber infrastructure' : 'Network monitoring & alerts'}</p>
                            </div>
                          </div>
                          {/* What this team handles */}
                          <div className="flex flex-wrap gap-1">
                            {(t.value === 'IMFT' ? ['No Internet', 'Slow Speed', 'Router', 'Install'] : t.value === 'BMFT' ? ['Fiber Cut', 'Splicing', 'Route Patrol'] : ['Alerts', 'Monitoring', 'Escalation']).map(tag => (
                              <span key={tag} className="text-[9px] bg-white/60 px-1.5 py-0.5 rounded">{tag}</span>
                            ))}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Workflow arrow */}
                  {form.assign_team && (
                    <div className="mt-3 p-3 rounded-lg bg-[var(--md-surface-1)] flex items-center gap-3 text-[12px]">
                      <Headset size={16} className="text-[var(--md-primary)] flex-shrink-0" />
                      <span>Operator creates ticket</span>
                      <CaretRight size={14} className="text-[var(--md-outline)]" />
                      <span className="font-bold text-[var(--md-primary)]">{TEAMS.find(t => t.value === form.assign_team)?.label}</span>
                      <CaretRight size={14} className="text-[var(--md-outline)]" />
                      <span>Technician receives on mobile</span>
                      <CaretRight size={14} className="text-[var(--md-outline)]" />
                      <span>Job completed</span>
                    </div>
                  )}
                </div>
                )}

                {/* Service Date Scheduling - Step 4 — Urgent/Require only */}
                {MANUAL_PRIORITIES.includes(form.priority) ? (
                <div className="border-2 border-[#FFCDD2] rounded-xl p-4 bg-[#FFF8F8]">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-[#B71C1C] text-white text-[10px] font-bold flex items-center justify-center">4</div>
                    <label className="text-[14px] font-medium text-[#B71C1C]">Manual Schedule (Required for {form.priority})</label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[12px] font-medium text-[var(--md-on-surface-variant)] mb-1.5 block">Service Date *</label>
                      <input type="date" value={form.service_date} onChange={e => loadAvailability(e.target.value)} className="md3-input h-11" data-testid="svc-date" min={new Date().toISOString().slice(0,10)} />
                    </div>
                    <div>
                      <label className="text-[12px] font-medium text-[var(--md-on-surface-variant)] mb-1.5 block">Time Slot</label>
                      <input type="time" value={form.service_time} onChange={e => setForm({...form, service_time: e.target.value})} className="md3-input h-11" />
                    </div>
                  </div>
                  {availability && (
                    <div className="mt-3 p-3 bg-[var(--md-primary-container)] rounded-xl text-[12px]">
                      <p className="font-medium text-[var(--md-on-primary-container)] mb-1">Availability for {availability.date}: {availability.total_remaining_slots} slots across {availability.total_available_teams} teams</p>
                      <div className="flex flex-wrap gap-2">
                        {availability.teams?.map(t => (
                          <span key={t.team_id} className={`px-2.5 py-1 rounded-lg text-[11px] ${t.available ? 'bg-white/70 text-[#1B5E20]' : 'bg-[#FFEBEE]/50 text-[#B71C1C] line-through'}`}>{t.team_name}: {t.remaining_slots}/{t.max_jobs}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                ) : (
                <div className="border border-[#C8E6C9] rounded-xl p-4 bg-[#F1F8E9]">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#2E7D32] text-white text-[10px] font-bold flex items-center justify-center">4</div>
                    <label className="text-[14px] font-medium text-[#2E7D32]">Auto-Schedule (System Managed)</label>
                  </div>
                  <p className="text-[12px] text-[#388E3C] mt-2">
                    🤖 System will automatically assign the best available team and date based on:
                    township grouping • workload balance • 7 sites/day limit
                  </p>
                  <p className="text-[11px] text-[#66BB6A] mt-1">No action needed — schedule will be set when ticket is created.</p>
                </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setSelectedCust(null); setTab('tickets'); }} className="md3-outlined-btn flex-1 py-3">Cancel</button>
                  <button type="submit" className="md3-filled-btn flex-1 py-3 text-[15px]" data-testid="submit-ticket-btn"><Headset size={18} className="mr-2 inline" />Create Support Ticket</button>
                </div>
              </form>
            )}

            {/* Empty state when no customer selected */}
            {!selectedCust && custResults.length === 0 && (
              <div className="bg-white rounded-xl border border-dashed border-[var(--md-outline-variant)] p-10 text-center">
                <MagnifyingGlass size={40} className="mx-auto text-[var(--md-outline)] mb-3" />
                <p className="text-sm text-[var(--md-on-surface-variant)]">Search for a customer above to begin creating a support ticket</p>
                <p className="text-[11px] text-[var(--md-outline)] mt-1">You can search by PPPoE Username, Phone Number, or Customer ID</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InfoCell({ label, value, color, mono }) {
  return (
    <div className="p-2.5 bg-[var(--md-surface-1)] rounded-lg">
      <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase tracking-wide">{label}</p>
      <p className={`text-[13px] font-medium mt-0.5 ${mono ? 'tabular-nums font-mono text-[12px]' : ''}`} style={color ? { color } : {}}>{value}</p>
    </div>
  );
}
